import {
  VARIANT_FOR_FEE_PRODUCT,
  addFeeProduct,
  eventNames,
  updateFeeProductQuantity,
  extractCartIdFromToken,
  writeCartMetaToCookie,
  clearCartMetaCookie,
  getCoreCartMetaCookie,
  auditRushFees,
  getCart,
} from "./cart.utils.js";

/**
 * Event listener for the ADD_ITEM event.
 *
 * @listens window:ADD_ITEM
 * @param {Event} event - The ADD_ITEM event object.
 */
window.addEventListener(eventNames.ADD_ITEM, ({ detail }) => {
  /**
   * Extracts the added items from the event detail.
   *
   * @type {object}
   */

  // Extract the response that was from addToCart call.
  const {
    data: { addToCartResponse },
  } = detail;

  // Extract the added items from the response.
  const { items: addedItems } = addToCartResponse;
  /**
   * Calls the addFeeProduct function to add a rush fee product to the cart.
   *
   * @param {array} addedItems - The array of added items.
   * @param {string} VARIANT_FOR_FEE_PRODUCT - The variant ID for the fee product pulled from the window object if it exists.
   */
  const isDualSaleProduct = addedItems.find(
    (item) =>
      item?.variant_title?.includes("Rush") ||
      item?.variant_options?.some((opt) => opt.includes("Rush")),
  );
  if (VARIANT_FOR_FEE_PRODUCT && isDualSaleProduct) {
    /**
     * If a Rush Fee product exists in store, we check if we need to add it depending on the the addedItems.
     * Initial `VARIANT_FOR_FEE_PRODUCT` helps initiate the liquid data. After product change, we'll need to grab the correct Rush Fee variant if there is a custom rush fee attached.
     */
    const rushFeeData = document.getElementById("rush-fee-variant")?.dataset;
    const { rushVariantId, defaultRushVariantId } = rushFeeData;
    const dynamicRushFeeVariant =
      rushVariantId !== "" ? rushVariantId : defaultRushVariantId;
    addFeeProduct(addedItems, dynamicRushFeeVariant, detail);
  } else {
    /**
     * If no Rush Fee exists in store, we are done and call rush fee sync complete
     */
    window.dispatchEvent(
      new CustomEvent(eventNames.RUSH_FEE_SYNC_COMPLETE, { detail }),
    );
  }
});

/**
 * Event listener for the REMOVE_ITEM event.
 *
 * @listens window:REMOVE_ITEM
 * @param {Event} event - The REMOVE_ITEM event object.
 */
window.addEventListener(eventNames.REMOVE_ITEM, ({ detail }) => {
  /**
   * Updates the quantity of the rush fee product in the cart.
   *
   * @param {object} detail.data - The data associated with the removed item.
   * @param {string} VARIANT_FOR_FEE_PRODUCT - The variant ID for the fee product pulled from the window object if it exists.
   */
  updateFeeProductQuantity(detail.data, VARIANT_FOR_FEE_PRODUCT);
});

/**
 * Event listener for the UPDATE_ITEM_QUANTITY event.
 *
 * @listens window:UPDATE_ITEM_QUANTITY
 * @param {Event} event - The UPDATE_ITEM_QUANTITY event object.
 */
window.addEventListener(eventNames.UPDATE_ITEM_QUANTITY, ({ detail }) => {
  /**
   * Updates the quantity of the rush fee product in the cart based on the updated item quantity.
   *
   * @param {object} detail.data - The data associated with the updated item.
   * @param {string} VARIANT_FOR_FEE_PRODUCT - The variant ID for the fee product pulled from the window object if it exists.
   */
  updateFeeProductQuantity(detail.data, VARIANT_FOR_FEE_PRODUCT);
});

/**
 * Event listener for the RUSH_FEE_SYNC_COMPLETE event.
 *
 * @listens window:RUSH_FEE_SYNC_COMPLETE
 * @param {Event} event - The RUSH_FEE_SYNC_COMPLETE event object.
 */
window.addEventListener(
  eventNames.RUSH_FEE_SYNC_COMPLETE,
  async ({ detail }) => {
    /**
     * When the rush fee sync is completed we can fire off any subsequent events.
     */
    try {
      /**
       * Get cart to pass into any events.
       *
       * We get the data here since scripts.js can't import getCart.
       */
      const cart = window.features.storefrontApiEnabled
        ? window.cart
        : await getCart();
      const updatedDetail = {
        ...detail,
        cart,
      };

      window.dispatchEvent(
        new CustomEvent(eventNames.CART_DATA_UPDATED, {
          detail: updatedDetail,
        }),
      );
    } catch (error) {
      console.error("Error during cart sync event:", error);
    }
  },
);

/**
 * Event listener for the CART_DATA_UPDATED EVENT.
 *
 * @listens window:CART_DATA_UPDATED
 */
window.addEventListener(eventNames.CART_DATA_UPDATED, async ({ detail }) => {
  const { cart } = detail;
  const { token, item_count: itemCount } = cart;
  const cartId = extractCartIdFromToken(token);

  if (!cartId) {
    return;
  }

  const meta = { hasItemsInCart: itemCount > 0 };
  writeCartMetaToCookie(cartId, meta);
});

/**
 * Event listener for checking and clearing the cart meta cookie on page load
 * Clears the cookie if there is either no current cart Id or the cart Ids do not match
 */
window.onload = () => {
  const cookie = getCoreCartMetaCookie();

  if (!cookie?.cartId) return;
  const { cart } = window;
  /**
   * Audit rush fees on load.
   *
   * Rush fees without associated products are removed, then the page is reloaded.
   */
  if (!window.features.storefrontApiEnabled) auditRushFees(cart);
  const cartId = extractCartIdFromToken(cart.token);

  if (!cartId || cookie.cartId !== cartId) {
    clearCartMetaCookie();
  }
};

/**
 * NOTE: We export these functions as a mechanism for rollup to acknowledge
 * these functions and ensure they are included in the minified build.
 * For some reason, without this export the functions are not included.
 */
export { addFeeProduct, eventNames, updateFeeProductQuantity };
